import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { ArrowRight, ShoppingCart, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';


const labels = {
  en: { logged_cta: 'Go to pricing', register_cta: 'Sign up and buy', price_from: 'from', success_title: 'Redirecting...', success_desc: 'You will be redirected.' },
  fr: { logged_cta: 'Voir les offres', register_cta: "S'inscrire et acheter", price_from: 'à partir de', success_title: 'Redirection...', success_desc: 'Vous allez être redirigé.' },
  es: { logged_cta: 'Ver ofertas', register_cta: 'Registrarse y comprar', price_from: 'desde', success_title: 'Redirigiendo...', success_desc: 'Será redirigido.' },
  de: { logged_cta: 'Angebote ansehen', register_cta: 'Registrieren und kaufen', price_from: 'ab', success_title: 'Weiterleitung...', success_desc: 'Sie werden weitergeleitet.' },
  it: { logged_cta: 'Vedi le offerte', register_cta: 'Iscriviti e acquista', price_from: 'da', success_title: 'Reindirizzamento...', success_desc: 'Sarai reindirizzato.' },
  pt: { logged_cta: 'Ver ofertas', register_cta: 'Registrar e comprar', price_from: 'a partir de', success_title: 'Redirecionando...', success_desc: 'Você será redirecionado.' },
  nl: { logged_cta: 'Bekijk aanbiedingen', register_cta: 'Registreer en koop', price_from: 'vanaf', success_title: 'Doorverwijzen...', success_desc: 'U wordt doorverwezen.' },
  pl: { logged_cta: 'Zobacz oferty', register_cta: 'Zarejestruj się i kup', price_from: 'od', success_title: 'Przekierowanie...', success_desc: 'Zostaniesz przekierowany.' },
  tr: { logged_cta: 'Teklifleri gör', register_cta: 'Kaydol ve satın al', price_from: 'den itibaren', success_title: 'Yönlendiriliyor...', success_desc: 'Yönlendirileceksiniz.' },
  ru: { logged_cta: 'Смотреть предложения', register_cta: 'Зарегистрироваться и купить', price_from: 'от', success_title: 'Перенаправление...', success_desc: 'Вы будете перенаправлены.' },
  ja: { logged_cta: '料金を見る', register_cta: '登録して購入', price_from: '', success_title: 'リダイレクト中...', success_desc: 'リダイレクトされます。' },
  zh: { logged_cta: '查看价格', register_cta: '注册并购买', price_from: '起', success_title: '正在跳转...', success_desc: '即将为您跳转。' },
  ar: { logged_cta: 'عرض الأسعار', register_cta: 'التسجيل والشراء', price_from: 'من', success_title: 'جارٍ التحويل...', success_desc: 'سيتم تحويلك.' },
  hi: { logged_cta: 'मूल्य देखें', register_cta: 'साइन अप और खरीदें', price_from: 'से', success_title: 'रीडायरेक्ट हो रहा है...', success_desc: 'आपको रीडायरेक्ट किया जाएगा।' },
};

const FALLBACK_PRICE = { eur: '4,99\u20ac', usd: '$5.49', gbp: '\u00a34.34', chf: 'CHF4.53', cad: 'C$7.92', jpy: '\u00a5913', aud: 'A$8.26' };

export const WaitlistForm = ({ variant = 'default' }) => {
  const { lang } = useI18n();
  const { user } = useAuth();
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const l = labels[lang] || labels.en;
  const priceDisplay = FALLBACK_PRICE[currency] || FALLBACK_PRICE.eur;

  const handleBuyNow = async () => {
    if (!user) {
      navigate('/login', { state: { redirect: '/pricing', message: 'signin_to_buy' } });
      return;
    }
    navigate('/pricing');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={variant === 'hero' ? '' : 'max-w-md mx-auto'}
      data-testid="waitlist-form"
    >
      <div className="flex flex-col items-center gap-3">
        <Button
          onClick={handleBuyNow}
          className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-3 text-base font-medium shadow-lg shadow-blue-500/20 transition-all hover:shadow-blue-500/30"
          data-testid="early-bird-buy-btn"
        >
          {user ? (
            <ShoppingCart className="w-4 h-4 mr-2" />
          ) : (
            <LogIn className="w-4 h-4 mr-2" />
          )}
          {user ? l.logged_cta : l.register_cta}
        </Button>
        <p className="text-muted-foreground/60 text-xs">{l.price_from} {priceDisplay}</p>
      </div>
    </motion.div>
  );
};
